/* eslint-disable max-lines */
import * as React from 'react';
import {
  Check,
  CircleNotch,
  Copy,
  Info,
  Warning,
  MagnifyingGlass,
  ArrowSquareOut,
} from '@phosphor-icons/react';
import { Badge, Button, Card, Stack, Text } from '@fragments-sdk/ui';
import { useQuery } from 'convex/react';
import { api } from '@/convex';
import { HighlightedCodeBlock } from '@/components/code/HighlightedCodeBlock';
import { WorkspacePage, WorkspacePageHeader } from '@/components/layout';
import { useOrgContext } from '@/hooks/useOrgContext';
import { useProjectContext } from '@/hooks/useProjectContext';
import { useClerkUser } from '@/hooks/useClerkUser';
import { useGitHubConnect } from '@/components/github/useGitHubConnect';
import { maskApiKey, useOrgApiKey } from '@/hooks/useOrgApiKey';
import { postJson, testClerkUserHeaders } from '@/lib/client-http';
import {
  WORKFLOW_TEMPLATE,
  WORKFLOW_PATH,
  CONFIG_FILENAME,
  GITHUB_SECRET_NAME,
} from '@/lib/govern-workflow';
import { useGovernSetup, type SetupState } from '@/hooks/useGovernSetup';
import {
  PLANS,
  cheapestPlanWithGovernanceAutomation,
  hasGovernanceAutomation,
  type PlanId,
} from '../../../../convex/billing/plans';
import s from './governance.module.scss';

// ── Shared hook ─────────────────────────────────────────────────

export function useCopyAction() {
  const [copied, setCopied] = React.useState<string | null>(null);
  const timeoutRef = React.useRef<number | null>(null);

  const copy = React.useCallback((label: string, text: string) => {
    navigator.clipboard?.writeText(text).catch(() => {});
    setCopied(label);
    if (timeoutRef.current) window.clearTimeout(timeoutRef.current);
    timeoutRef.current = window.setTimeout(() => setCopied(null), 1600);
  }, []);

  return { copied, copy };
}

// ── Detect hook ─────────────────────────────────────────────────

type DetectResult = {
  status: string;
  hasWorkflow: boolean;
  hasConfig: boolean;
};

type DetectState =
  | { phase: 'idle' }
  | { phase: 'running' }
  | { phase: 'done'; result: DetectResult }
  | { phase: 'error'; message: string };

function useGovernDetect(
  orgId: string | undefined,
  projectId: string | undefined,
  repoFullName: string | undefined,
) {
  const [state, setState] = React.useState<DetectState>({ phase: 'idle' });
  const { user } = useClerkUser();

  const detect = React.useCallback(async () => {
    if (!orgId || !projectId || !repoFullName) return;
    setState({ phase: 'running' });
    try {
      const data = await postJson<DetectResult>(
        '/api/govern/detect',
        { orgId, projectId, repoFullName },
        'Detection failed',
        { headers: testClerkUserHeaders(user?.id) },
      );
      setState({ phase: 'done', result: data as DetectResult });
    } catch (err) {
      setState({
        phase: 'error',
        message: err instanceof Error ? err.message : 'Network error',
      });
    }
  }, [orgId, projectId, repoFullName, user?.id]);

  return { state, detect };
}

// ── SetupCard with confirmation dialog ──────────────────────────

export function SetupCard({
  repoFullName,
  setupState,
  onConfirm,
  apiKey,
}: {
  repoFullName: string;
  setupState: SetupState;
  onConfirm: () => void;
  apiKey: string | null;
}) {
  const dialogRef = React.useRef<HTMLDialogElement>(null);
  const maskedKey = maskApiKey(apiKey);

  return (
    <>
      <Card
        as="section"
        variant="outlined"
        padding="none"
        className={s.autoSetupCard}
      >
        <Stack
          direction={{ base: 'column', md: 'row' }}
          justify="between"
          gap="md"
          className={s.autoSetupTop}
        >
          <Stack className={s.autoSetupInfo} gap="xs">
            <Text
              as="h2"
              size="sm"
              weight="semibold"
              className={s.stepTitle}
            >
              Enable CI governance for{' '}
              <code className={s.inlineCode}>{repoFullName}</code>
            </Text>
            <Text
              as="p"
              size="xs"
              color="secondary"
              className={s.stepSubtitle}
            >
              Creates a repo-owned fragments.config.ts when needed, adds the
              workflow and API key, and opens a PR for review.
            </Text>
          </Stack>
          <Button
            type="button"
            size="sm"
            className={s.primaryAction}
            disabled={setupState.phase === 'running'}
            onClick={() => dialogRef.current?.showModal()}
          >
            {setupState.phase === 'running' ? (
              <>
                <CircleNotch size={14} className={s.spinIcon} />
                Setting up&hellip;
              </>
            ) : (
              'Enable governance'
            )}
          </Button>
        </Stack>
        {setupState.phase === 'error' && (
          <Text
            as="p"
            size="xs"
            color="secondary"
            className={s.autoSetupError}
          >
            {setupState.message}
          </Text>
        )}
      </Card>
      <div className={s.autoSetupOr}>
        <Text as="span" size="xs" color="tertiary">
          or set up manually
        </Text>
      </div>

      <dialog ref={dialogRef} className={s.confirmDialog}>
        <Stack className={s.confirmInner} gap="md">
          <Text
            as="h2"
            size="lg"
            weight="semibold"
            className={s.confirmTitle}
          >
            Enable governance CI
          </Text>
          <Text
            as="p"
            size="sm"
            color="secondary"
            className={s.confirmDesc}
          >
            This will create a pull request on{' '}
            <code className={s.inlineCode}>{repoFullName}</code> with the
            following changes:
          </Text>
          <ul className={s.confirmList}>
            <li>
              <code className={s.inlineCode}>{WORKFLOW_PATH}</code>
              <Text
                as="span"
                size="xs"
                color="secondary"
                className={s.confirmListDesc}
              >
                GitHub Actions workflow that runs{' '}
                <code className={s.inlineCode}>fragments check</code> on every
                push and PR
              </Text>
            </li>
            <li>
              <Text
                as="span"
                size="xs"
                color="secondary"
                className={s.confirmListDesc}
              >
                Rules managed from Fragments Cloud — add a{' '}
                <code className={s.inlineCode}>{CONFIG_FILENAME}</code> to
                override locally
              </Text>
            </li>
          </ul>
          {maskedKey && (
            <Text
              as="p"
              size="xs"
              color="secondary"
              className={s.confirmNote}
            >
              The API key (<code className={s.inlineCode}>{maskedKey}</code>)
              will be added as a repository secret named{' '}
              <code className={s.inlineCode}>{GITHUB_SECRET_NAME}</code>.
            </Text>
          )}
          <Stack direction="row" gap="sm" className={s.confirmActions}>
            <Button
              type="button"
              variant="outlined"
              size="sm"
              onClick={() => dialogRef.current?.close()}
            >
              Cancel
            </Button>
            <Button
              type="button"
              size="sm"
              className={s.primaryAction}
              onClick={() => {
                dialogRef.current?.close();
                onConfirm();
              }}
            >
              Create pull request
            </Button>
          </Stack>
        </Stack>
      </dialog>
    </>
  );
}

// ── Pending PR state ─────────────────────────────────────────────

function PendingPrBanner({ prUrl }: { prUrl: string | undefined }) {
  return (
    <Card
      as="section"
      variant="outlined"
      padding="none"
      className={s.autoSetupDone}
    >
      <CircleNotch size={20} className={s.spinIcon} />
      <Stack gap="xs">
        <Text
          as="h2"
          size="sm"
          weight="semibold"
          className={s.stepTitle}
        >
          Pull request is open
        </Text>
        <Text
          as="p"
          size="xs"
          color="secondary"
          className={s.stepSubtitle}
        >
          Merge the PR to activate governance checks. This page updates
          automatically once the PR is merged.
        </Text>
        <div className={s.successActions}>
          {prUrl && (
            <Button
              as="a"
              href={prUrl}
              target="_blank"
              rel="noopener noreferrer"
              variant="link"
              size="xs"
            >
              View pull request &rarr;
            </Button>
          )}
        </div>
      </Stack>
    </Card>
  );
}

// ── Broken state ────────────────────────────────────────────────

function BrokenBanner({
  repoFullName,
  onDetect,
  detecting,
  detectResult,
}: {
  repoFullName: string | undefined;
  onDetect: () => void;
  detecting: boolean;
  detectResult?: DetectResult;
}) {
  const missing: string[] = [];
  if (detectResult && !detectResult.hasWorkflow) missing.push(WORKFLOW_PATH);

  return (
    <Card
      as="section"
      variant="outlined"
      padding="none"
      className={s.brokenBanner}
    >
      <Warning size={20} weight="fill" className={s.brokenBannerIcon} />
      <Stack gap="xs">
        <Text
          as="h2"
          size="sm"
          weight="semibold"
          className={s.stepTitle}
        >
          Governance configuration incomplete
        </Text>
        <Text
          as="p"
          size="xs"
          color="secondary"
          className={s.stepSubtitle}
        >
          {missing.length > 0 ? (
            <>
              Missing{' '}
              {missing.map((f, i) => (
                <React.Fragment key={f}>
                  {i > 0 && ' and '}
                  <code className={s.inlineCode}>{f}</code>
                </React.Fragment>
              ))}{' '}
              in <code className={s.inlineCode}>{repoFullName}</code>. Re-add
              the missing files or run the setup again.
            </>
          ) : (
            <>
              Required configuration files are missing from{' '}
              <code className={s.inlineCode}>{repoFullName}</code>.
            </>
          )}
        </Text>
        <div className={s.successActions}>
          <Button
            type="button"
            variant="outlined"
            size="xs"
            className={s.rowAction}
            disabled={detecting}
            onClick={onDetect}
          >
            {detecting ? (
              <>
                <CircleNotch size={12} className={s.spinIcon} />
                Checking&hellip;
              </>
            ) : (
              <>
                <MagnifyingGlass size={12} />
                Re-check repository
              </>
            )}
          </Button>
        </div>
      </Stack>
    </Card>
  );
}

// ── Setup Guide (empty state) ───────────────────────────────────
export function GovernanceSetupGuide() {
  const { orgId, org, activeRole } = useOrgContext();
  const { selectedProjectId, selectedProject } = useProjectContext();
  const plan = (org?.plan ?? 'free') as PlanId;
  const canUseGovernanceAutomation = hasGovernanceAutomation(plan);
  const isAdmin = activeRole === 'owner' || activeRole === 'admin';
  const { copied, copy } = useCopyAction();

  const ciStatus = selectedProject?.governanceCiStatus;
  const ciPrUrl = selectedProject?.governanceCiPrUrl;

  const binding = useQuery(
    api.projects.getPrimaryBinding,
    selectedProjectId ? { projectId: selectedProjectId } : 'skip',
  );

  const {
    isConnected: hasGitHub,
    isAppInstalled,
    appInstallUrl,
  } = useGitHubConnect({
    orgId,
    projectId: selectedProjectId,
    source: 'settings',
  });

  const repoFullName = binding?.repoFullName;

  const { apiKey } = useOrgApiKey({
    orgId,
    enabled: isAdmin && canUseGovernanceAutomation,
  });
  // Auto-setup no longer needs the org key client-side — the route mints a
  // scoped CI token server-side.
  const canAutoSetup =
    canUseGovernanceAutomation && hasGitHub && !!repoFullName && isAdmin;

  const { state: setupState, run: runSetup } = useGovernSetup(
    orgId as string | undefined,
    selectedProjectId as string | undefined,
    repoFullName,
  );

  const { state: detectState, detect } = useGovernDetect(
    orgId as string | undefined,
    selectedProjectId as string | undefined,
    repoFullName,
  );

  const maskedKey = maskApiKey(apiKey);

  const isPendingPr = ciStatus === 'pending_pr' || setupState.phase === 'done';
  const isBroken = ciStatus === 'broken';
  const showManualSteps = !isPendingPr && !isBroken;

  return (
    <WorkspacePage className={s.setupScroll}>
      <WorkspacePageHeader
        title="Governance"
        description="Run governance checks in your CI pipeline and report violations to Fragments Cloud. Safety, token, accessibility, and component policy violations appear here automatically."
        meta={selectedProject?.name}
      />
      <div className={s.setupGuide}>
        {isBroken && canUseGovernanceAutomation && (
          <BrokenBanner
            repoFullName={repoFullName}
            onDetect={detect}
            detecting={detectState.phase === 'running'}
            detectResult={
              detectState.phase === 'done' ? detectState.result : undefined
            }
          />
        )}

        {isPendingPr && (
          <PendingPrBanner
            prUrl={setupState.phase === 'done' ? setupState.prUrl : ciPrUrl}
          />
        )}

        {!isPendingPr && !isBroken && (
          <Card
            as="section"
            variant="outlined"
            padding="none"
            className={s.complementBanner}
          >
            <Info size={18} className={s.complementIcon} weight="fill" />
            <Stack className={s.complementText} gap="xs">
              <Text
                as="span"
                size="sm"
                weight="semibold"
                className={s.complementTitle}
              >
                Complements your existing setup
              </Text>
              <Text
                as="span"
                size="xs"
                color="secondary"
                className={s.complementDesc}
              >
                Fragments Cloud already detects hardcoded tokens and duplicate
                values when you connect a repository. CI governance adds
                policy-based checks — safety, accessibility, component usage,
                and brand enforcement — on every push and PR.
              </Text>
            </Stack>
          </Card>
        )}

        {!canUseGovernanceAutomation && !isPendingPr && (
          <Card
            as="section"
            variant="outlined"
            padding="none"
            className={s.planLockPanel}
          >
            <div className={s.planLockIcon}>
              <Warning size={18} weight="fill" />
            </div>
            <Stack className={s.planLockCopy} gap="sm">
              <div className={s.planLockTitleRow}>
                <Text as="h2" size="sm" weight="semibold">
                  Cloud governance automation requires Pro
                </Text>
                <Badge variant="outline" size="sm">
                  {PLANS[plan].label}
                </Badge>
              </div>
              <Text as="p" size="xs" color="secondary">
                Free workspaces can evaluate rules locally. CI reporting, GitHub
                Check Runs, PR summary comments, and inline finding comments
                require{' '}
                {PLANS[cheapestPlanWithGovernanceAutomation() ?? 'pro'].label}.
              </Text>
              <HighlightedCodeBlock
                code={`npm install --save-dev @fragments-sdk/cli\nnpx fragments init\nnpx fragments check --format summary`}
                language="bash"
                className={s.codeBlock}
                lazy={false}
              />
            </Stack>
          </Card>
        )}

        {canUseGovernanceAutomation &&
          hasGitHub &&
          !isAppInstalled &&
          appInstallUrl &&
          showManualSteps && (
            <Card
              as="section"
              variant="outlined"
              padding="none"
              className={s.appInstallPrompt}
            >
              <Info
                size={16}
                weight="fill"
                className={s.appInstallPromptIcon}
              />
              <Stack className={s.appInstallPromptContent} gap="xs">
                <Text
                  as="span"
                  size="xs"
                  weight="semibold"
                  className={s.appInstallPromptTitle}
                >
                  Install the Fragments CI GitHub App
                </Text>
                <Text
                  as="span"
                  size="xs"
                  color="secondary"
                  className={s.appInstallPromptDesc}
                >
                  For branded bot comments on PRs and automatic drift detection.
                  Not required, but recommended.
                </Text>
              </Stack>
              <Button
                as="a"
                href={appInstallUrl}
                target="_blank"
                rel="noopener noreferrer"
                variant="outlined"
                size="xs"
                className={s.appInstallPromptBtn}
              >
                Install
                <ArrowSquareOut size={12} />
              </Button>
            </Card>
          )}

        {canAutoSetup && showManualSteps && (
          <SetupCard
            repoFullName={repoFullName!}
            setupState={setupState}
            onConfirm={runSetup}
            apiKey={apiKey}
          />
        )}

        {showManualSteps && canUseGovernanceAutomation && (
          <>
            <Stack className={s.manualSteps} gap="md">
              <Card
                as="section"
                variant="outlined"
                padding="none"
                className={s.stepRow}
              >
                <Text
                  as="span"
                  size="xs"
                  weight="semibold"
                  className={s.stepNumber}
                >
                  1
                </Text>
                <Stack className={s.stepContent} gap="sm">
                  <div className={s.stepTitleRow}>
                    <div>
                      <Text
                        as="h2"
                        size="sm"
                        weight="semibold"
                        className={s.stepTitle}
                      >
                        Install and initialize
                      </Text>
                      <Text
                        as="p"
                        size="xs"
                        color="secondary"
                        className={s.stepSubtitle}
                      >
                        Run these in your project root
                      </Text>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="xs"
                      className={s.copyBtn}
                      onClick={() =>
                        copy(
                          'install',
                          'npm install --save-dev @fragments-sdk/cli && npx fragments init',
                        )
                      }
                    >
                      {copied === 'install' ? (
                        <Check size={13} weight="bold" />
                      ) : (
                        <Copy size={13} />
                      )}
                      {copied === 'install' ? 'Copied' : 'Copy'}
                    </Button>
                  </div>
                  <HighlightedCodeBlock
                    code={`npm install --save-dev @fragments-sdk/cli\nnpx fragments init`}
                    language="bash"
                    className={s.codeBlock}
                    lazy={false}
                  />
                  <Text
                    as="p"
                    size="xs"
                    color="secondary"
                    className={s.stepText}
                  >
                    <code className={s.inlineCode}>fragments init</code>{' '}
                    generates a{' '}
                    <code className={s.inlineCode}>fragments.config.ts</code>{' '}
                    with a governance policy. It auto-detects Tailwind, SCSS,
                    and CSS variable token sources.
                  </Text>
                </Stack>
              </Card>

              <Card
                as="section"
                variant="outlined"
                padding="none"
                className={s.stepRow}
              >
                <Text
                  as="span"
                  size="xs"
                  weight="semibold"
                  className={s.stepNumber}
                >
                  2
                </Text>
                <Stack className={s.stepContent} gap="sm">
                  <Text
                    as="h2"
                    size="sm"
                    weight="semibold"
                    className={s.stepTitle}
                  >
                    Add API key to GitHub Secrets
                  </Text>
                  <Text
                    as="p"
                    size="xs"
                    color="secondary"
                    className={s.stepSubtitle}
                  >
                    Go to your repo Settings &rarr; Secrets &rarr; Actions
                    &rarr; New repository secret
                  </Text>
                  <div className={s.keyRow}>
                    <div className={s.keyField}>
                      <Text
                        as="span"
                        size="2xs"
                        weight="semibold"
                        color="tertiary"
                        className={s.keyLabel}
                      >
                        Name
                      </Text>
                      <code className={s.keyValue}>{GITHUB_SECRET_NAME}</code>
                    </div>
                    <div className={s.keyField}>
                      <Text
                        as="span"
                        size="2xs"
                        weight="semibold"
                        color="tertiary"
                        className={s.keyLabel}
                      >
                        Value
                      </Text>
                      {apiKey ? (
                        <Button
                          type="button"
                          variant="outlined"
                          size="xs"
                          className={s.keyValueCopyable}
                          onClick={() => copy('key', apiKey)}
                        >
                          <code>{maskedKey}</code>
                          {copied === 'key' ? (
                            <Check size={12} weight="bold" />
                          ) : (
                            <Copy size={12} />
                          )}
                        </Button>
                      ) : (
                        <code className={s.keyValue}>
                          {isAdmin
                            ? 'Loading\u2026'
                            : 'Ask an admin to reveal the API key'}
                        </code>
                      )}
                    </div>
                  </div>
                </Stack>
              </Card>

              <Card
                as="section"
                variant="outlined"
                padding="none"
                className={s.stepRow}
              >
                <Text
                  as="span"
                  size="xs"
                  weight="semibold"
                  className={s.stepNumber}
                >
                  3
                </Text>
                <Stack className={s.stepContent} gap="sm">
                  <div className={s.stepTitleRow}>
                    <div>
                      <Text
                        as="h2"
                        size="sm"
                        weight="semibold"
                        className={s.stepTitle}
                      >
                        Add the workflow file
                      </Text>
                      <Text
                        as="p"
                        size="xs"
                        color="secondary"
                        className={s.stepSubtitle}
                      >
                        Save as{' '}
                        <code className={s.inlineCode}>{WORKFLOW_PATH}</code>
                      </Text>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="xs"
                      className={s.copyBtn}
                      onClick={() => copy('workflow', WORKFLOW_TEMPLATE)}
                    >
                      {copied === 'workflow' ? (
                        <Check size={13} weight="bold" />
                      ) : (
                        <Copy size={13} />
                      )}
                      {copied === 'workflow' ? 'Copied' : 'Copy'}
                    </Button>
                  </div>
                  <HighlightedCodeBlock
                    code={WORKFLOW_TEMPLATE}
                    language="yaml"
                    className={s.codeBlock}
                    lazy={false}
                  />
                  <Text
                    as="p"
                    size="xs"
                    color="secondary"
                    className={s.stepText}
                  >
                    The scan parses JSX/TSX files, validates against your
                    policy, and reports violations here. The workflow exits
                    non-zero when violations are found.
                  </Text>
                </Stack>
              </Card>
            </Stack>
          </>
        )}

        {canUseGovernanceAutomation &&
          hasGitHub &&
          !!repoFullName &&
          showManualSteps && (
            <div className={s.detectRow}>
              <Button
                type="button"
                variant="outlined"
                size="xs"
                className={s.rowAction}
                disabled={detectState.phase === 'running'}
                onClick={detect}
              >
                {detectState.phase === 'running' ? (
                  <>
                    <CircleNotch size={12} className={s.spinIcon} />
                    Checking&hellip;
                  </>
                ) : (
                  <>
                    <MagnifyingGlass size={12} />
                    I&apos;ve set it up manually
                  </>
                )}
              </Button>
              {detectState.phase === 'done' &&
                detectState.result.status !== 'active' && (
                  <Text
                    as="span"
                    size="xs"
                    color="secondary"
                    className={s.detectHint}
                  >
                    {!detectState.result.hasWorkflow
                      ? `Missing ${WORKFLOW_PATH}.`
                      : 'Workflow found but governance is not reporting.'}
                  </Text>
                )}
              {detectState.phase === 'error' && (
                <Text
                  as="span"
                  size="xs"
                  color="secondary"
                  className={s.detectHint}
                >
                  {detectState.message}
                </Text>
              )}
            </div>
          )}

        <Card
          as="section"
          variant="outlined"
          padding="none"
          className={s.stepRow}
        >
          <Stack className={s.stepContent} gap="sm">
            <Text
              as="h2"
              size="sm"
              weight="semibold"
              className={s.stepTitle}
            >
              What gets checked
            </Text>
            <div className={s.validatorList}>
              <div className={s.validatorRow}>
                <Text
                  as="span"
                  size="2xs"
                  weight="semibold"
                  className={s.categoryPill}
                  data-category="tokens"
                >
                  Hardcoded Values
                </Text>
                <Text
                  as="span"
                  size="xs"
                  color="secondary"
                  className={s.validatorDesc}
                >
                  Detects hardcoded colors, spacing, radii, and typography that
                  should reference design tokens
                </Text>
              </div>
              <div className={s.validatorRow}>
                <Text
                  as="span"
                  size="2xs"
                  weight="semibold"
                  className={s.categoryPill}
                  data-category="a11y"
                >
                  Accessibility
                </Text>
                <Text
                  as="span"
                  size="xs"
                  color="secondary"
                  className={s.validatorDesc}
                >
                  WCAG AA compliance — missing labels, roles, contrast ratios
                </Text>
              </div>
              <div className={s.validatorRow}>
                <Text
                  as="span"
                  size="2xs"
                  weight="semibold"
                  className={s.categoryPill}
                  data-category="components"
                >
                  Component Usage
                </Text>
                <Text
                  as="span"
                  size="xs"
                  color="secondary"
                  className={s.validatorDesc}
                >
                  Validates components against your design system registry —
                  allowlists, required children, and prop correctness
                </Text>
              </div>
            </div>
          </Stack>
        </Card>
      </div>
    </WorkspacePage>
  );
}
